import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Tooltip from './Tooltip'

const LINES_CLASS = {
  1: 'tw:line-clamp-1',
  2: 'tw:line-clamp-2',
  3: 'tw:line-clamp-3'
}

const isOverflowing = element => element.scrollHeight > element.clientHeight + 1
  || element.scrollWidth > element.clientWidth + 1

const extractText = node => {
  if (typeof node === 'string' || typeof node === 'number')
    return String(node)
  if (Array.isArray(node))
    return node.map(extractText).join('')
  if (React.isValidElement(node))
    return extractText(node.props.children)
  return ''
}

const Text = ({
  asElement = 'span',
  lines = null,
  truncate = false,
  className = '',
  wrapperClassName = '',
  children,
  ...rest
}) => {
  const ref = useRef(null)
  const [overflow, setOverflow] = useState(false)
  const clipping = truncate || Boolean(lines)

  useLayoutEffect(() => {
    const el = ref.current
    if (!clipping || !el)
      return null
    const update = () => setOverflow(isOverflowing(el))
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [truncate, lines])

  const tooltipContent = useMemo(() => {
    if (!overflow)
      return null
    return extractText(children)
  }, [overflow, children])

  const Component = asElement
  const truncateClass = truncate ? 'tw:truncate' : ''
  const finalClassName = [className, LINES_CLASS[lines] || '', truncateClass]
    .filter(Boolean).join(' ')

  const element = (
    <Component ref={ref} className={finalClassName} {...rest}>
      {children}
    </Component>
  )

  if (!clipping)
    return element

  return <Tooltip className={wrapperClassName} content={tooltipContent}>{element}</Tooltip>
}

Text.propTypes = {
  asElement: PropTypes.oneOf(['span', 'p', 'div', 'strong', 'em', 'small', 'label']),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  lines: PropTypes.oneOf([1, 2, 3]),
  truncate: PropTypes.bool,
  wrapperClassName: PropTypes.string
}

Text.defaultProps = {
  asElement: 'span',
  className: '',
  lines: null,
  truncate: false,
  wrapperClassName: ''
}

export default Text
